<template>
  <div class="rounded p-1">
    <div class="mt-4">
      <div
        v-for="(day, index) in orderedWeeklySchedule"
        :key="index"
        class="flex w-full flex-row"
        :class="{
          'items-center py-3': !day.isActive,
          '!border-b border-gray-200':
            index !== schedule.getWeeklySchedule().length - 1,
        }"
      >
        <UICheckbox
          :id="day.day"
          :key="day.day"
          class="mt-[0.75rem] w-[16%] min-w-[90px]"
          :class="{ '!mt-0': !day.isActive }"
          :name="day.day"
          :checked="day.isActive"
          @change="emit('toggle-day-enabled', day)"
        >
          {{ capitalizeAbbr(day.day || '') }}
        </UICheckbox>

        <div v-if="day.isActive" class="mt-3 w-[84%]">
          <div
            v-for="(slot, intervalIndex) in day.intervals"
            :key="intervalIndex"
            class="mb-1 flex flex-col items-start gap-2"
          >
            <div class="flex w-full flex-row space-x-2">
              <div class="flex w-[37%] flex-col gap-1">
                <span v-if="intervalIndex === 0" class="mx-2 truncate" :title="t('settings.availability.schedule.start_time')">{{
                  t('settings.availability.schedule.start_time')
                }}</span>
                <UITimePicker
                  v-model:formatted-value="slot.from"
                  class="w-full"
                  value-format="H:m"
                  format="hh:mm a"
                  :minutes="5"
                  use-12-hours
                  :actions="['confirm']"
                  :is-hour-disabled="
                    hour => handleIsHourDisabled(hour, null, slot.to)
                  "
                  :is-minute-disabled="
                    (min, hour) => handleIsMinDisabled(min, hour, null, slot.to)
                  "
                  @update:formatted-value="emit('time-changed')"
                />
              </div>
              <div class="flex w-[37%] flex-col gap-1">
                <span v-if="intervalIndex === 0" class="mx-2 truncate" :title="t('settings.availability.schedule.end_time')">{{
                  t('settings.availability.schedule.end_time')
                }}</span>
                <UITimePicker
                  v-model:formatted-value="slot.to"
                  class="w-full"
                  value-format="H:m"
                  format="hh:mm a"
                  :minutes="5"
                  use-12-hours
                  :actions="['confirm']"
                  :is-hour-disabled="
                    hour => handleIsHourDisabled(hour, slot.from, null)
                  "
                  :is-minute-disabled="
                    (min, hour) =>
                      handleIsMinDisabled(min, hour, slot.from, null)
                  "
                  @update:formatted-value="emit('time-changed')"
                />
              </div>
              <div
                class="ml-4 flex w-[26%] gap-2"
                :class="{ 'mt-4 pt-2': intervalIndex === 0 }"
              >
                <UIButton
                  v-if="intervalIndex === day.intervals.length - 1"
                  id="add-availability-hour-std"
                  :aria-label="
                    t('settings.availability.schedule.add_availability_hour')
                  "
                  :text="true"
                  class="px-1 text-gray-700"
                  :disabled="day.is24HourSelected()"
                  @click="
                    day.addNewAvailabilityHourWithStartTimeAndEndTime(
                      day.intervals[day.intervals.length - 1]?.to,
                      day.intervals[day.intervals.length - 1]?.to
                    )
                  "
                >
                  <PlusIcon class="h-4 w-4" />
                </UIButton>
                <UIButton
                  v-if="day.intervals"
                  :aria-label="
                    t('settings.availability.schedule.remove_availability_hour')
                  "
                  class="px-1 text-gray-700"
                  :text="true"
                  @click="emit('remove-time-slot', day, intervalIndex)"
                >
                  <Trash01Icon class="h-4 w-4" />
                </UIButton>
                <div class="relative">
                  <UIPopover
                    v-if="intervalIndex == 0"
                    trigger="click"
                    :show="
                      activeHourIndexForCopy === index && intervalIndex == 0
                    "
                    class="!rounded-md !pb-0"
                    @clickoutside="
                      () => {
                        copyToDays = []
                        activeHourIndexForCopy = -1
                      }
                    "
                  >
                    <template #trigger>
                      <UIButton
                        id="copy-availability-hour-std"
                        :aria-label="$t('aria_labels.copy_availability_hour')"
                        class="mt-2.5 px-1 text-gray-700"
                        :text="true"
                        @click="
                          () => {
                            activeHourIndexForCopy > -1
                              ? (activeHourIndexForCopy = -1)
                              : (activeHourIndexForCopy = index)
                          }
                        "
                      >
                        <Copy06Icon class="h-4 w-4" />
                      </UIButton>
                    </template>
                    <div class="w-40">
                      <p class="text-md mt-0 px-0.5 font-normal text-gray-600">
                        {{
                          t('calendar_advanced.availability.copy_times_to')
                        }}...
                      </p>
                      <UICheckbox
                        :id="'All Days'"
                        :key="'All Days'"
                        :name="'All Days'"
                        :checked="copyToDays.length === 7"
                        class="text-md flex w-20 p-2"
                        @change="
                          x =>
                            x
                              ? (copyToDays = [...orderedScheduleDays])
                              : (copyToDays = [])
                        "
                      >
                        <div
                          class="font-md mr-32 w-full font-semibold text-gray-700"
                        >
                          {{ t('calendar_advanced.availability.copy_to_all') }}
                        </div>
                      </UICheckbox>
                      <div
                        v-for="(w_day, k) in orderedScheduleDays"
                        :key="k"
                        class="flex items-center p-2"
                      >
                        <UICheckbox
                          :id="w_day"
                          :key="w_day"
                          :name="w_day"
                          :checked="copyToDays.includes(w_day)"
                          :disabled="w_day === day.day"
                          class="text-md flex w-20"
                          @change="
                            x => {
                              x
                                ? copyToDays.push(w_day)
                                : copyToDays.splice(
                                    copyToDays.indexOf(w_day),
                                    1
                                  )
                            }
                          "
                        >
                          <div
                            class="font-md mr-32 w-full font-semibold capitalize text-gray-700"
                          >
                            {{ w_day }}
                          </div>
                        </UICheckbox>
                      </div>
                      <UIButton
                        id="submit"
                        type="primary"
                        size="small"
                        class="my-2 w-full text-center"
                        @click="
                          () => {
                            schedule.copyScheduleToMultipleDays(
                              day?.day || '',
                              copyToDays
                            )
                            copyToDays = []
                            activeHourIndexForCopy = -1
                          }
                        "
                      >
                        {{ t('calendar_advanced.availability.apply') }}
                      </UIButton>
                    </div>
                  </UIPopover>
                </div>
              </div>
            </div>
            <div>
              <p
                v-if="day?.hasOverlapIndex(intervalIndex)"
                class="text-left text-sm text-red-600"
              >
                {{ t('calendar_advanced.availability.overlap_error') }}
              </p>
            </div>
          </div>
        </div>
        <div v-else class="text-gray-600">
          {{ t('settings.availability.schedule.unavailable') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Copy06Icon,
  PlusIcon,
  Trash01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UICheckbox,
  UIPopover,
  UITimePicker,
} from '@gohighlevel/ghl-ui'
import { computed, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  getAppStartDayOfWeek,
  getOrderedScheduleDays,
  getScheduleDayIndex,
} from '../../utils/weekStart'
import { DayAvailability } from '../Schedule/DayAvailability'
import { Schedule } from '../Schedule/Schedule'

const { t } = useI18n()

const props = defineProps({
  schedule: { type: Object as PropType<Schedule>, required: true },
  capitalizeAbbr: { type: Function, required: true },
  handleIsHourDisabled: { type: Function, required: true },
  handleIsMinDisabled: { type: Function, required: true },
})

const emit = defineEmits<{
  (e: 'toggle-day-enabled', day: DayAvailability): void
  (e: 'remove-time-slot', day: DayAvailability, intervalIndex: number): void
  (e: 'time-changed'): void
}>()

const copyToDays = ref<string[]>([])
const activeHourIndexForCopy = ref(-1)
const appStartDayOfWeek = computed(() => getAppStartDayOfWeek())
const orderedScheduleDays = computed(() =>
  getOrderedScheduleDays(appStartDayOfWeek.value)
)
const orderedWeeklySchedule = computed(() =>
  [...props.schedule.getWeeklySchedule()].sort(
    (a, b) =>
      getScheduleDayIndex(appStartDayOfWeek.value, a.day || '') -
      getScheduleDayIndex(appStartDayOfWeek.value, b.day || '')
  )
)
</script>
